package com.evoapps.evoshift.ui.workplaces

import androidx.compose.foundation.layout.Arrangement
import androidx.compose.foundation.layout.BoxWithConstraints
import androidx.compose.foundation.layout.Column
import androidx.compose.foundation.layout.ColumnScope
import androidx.compose.foundation.layout.Row
import androidx.compose.foundation.layout.Spacer
import androidx.compose.foundation.layout.fillMaxWidth
import androidx.compose.foundation.layout.size
import androidx.compose.foundation.layout.width
import androidx.compose.material.icons.Icons
import androidx.compose.material.icons.filled.EventNote
import androidx.compose.material.icons.filled.Restore
import androidx.compose.material3.Icon
import androidx.compose.material3.MaterialTheme
import androidx.compose.material3.Text
import androidx.compose.material3.TextButton
import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.setValue
import androidx.compose.ui.Alignment
import androidx.compose.ui.Modifier
import androidx.compose.ui.graphics.Color
import androidx.compose.ui.platform.testTag
import androidx.compose.ui.semantics.contentDescription
import androidx.compose.ui.semantics.heading
import androidx.compose.ui.semantics.semantics
import androidx.compose.ui.text.font.FontWeight
import androidx.compose.ui.text.input.KeyboardType
import androidx.compose.ui.unit.Dp
import androidx.compose.ui.unit.dp
import com.evoapps.evoshift.domain.model.RequestSettings
import com.evoapps.evoshift.ui.LightGold
import com.evoapps.evoshift.ui.adaptive.handCursor
import com.evoapps.evoshift.widgets.DialogAction
import com.evoapps.evoshift.widgets.EvoDialog
import com.evoapps.evoshift.widgets.EvoTextField
import evoshift.composeapp.generated.resources.Res
import evoshift.composeapp.generated.resources.save
import evoshift.composeapp.generated.resources.settings_blood_donation_days
import evoshift.composeapp.generated.resources.settings_care_allowance_days
import evoshift.composeapp.generated.resources.settings_care_child
import evoshift.composeapp.generated.resources.settings_care_family
import evoshift.composeapp.generated.resources.settings_care_total
import evoshift.composeapp.generated.resources.settings_carer_leave_days
import evoshift.composeapp.generated.resources.settings_childcare_limit
import evoshift.composeapp.generated.resources.settings_force_majeure_limit
import evoshift.composeapp.generated.resources.settings_help_requests
import evoshift.composeapp.generated.resources.settings_limit_blood_field
import evoshift.composeapp.generated.resources.settings_limit_explain_blood
import evoshift.composeapp.generated.resources.settings_limit_explain_care
import evoshift.composeapp.generated.resources.settings_limit_explain_carer
import evoshift.composeapp.generated.resources.settings_limit_explain_childcare
import evoshift.composeapp.generated.resources.settings_limit_explain_force_majeure
import evoshift.composeapp.generated.resources.settings_limit_year_field
import evoshift.composeapp.generated.resources.settings_limits_restore
import evoshift.composeapp.generated.resources.settings_limits_restore_for
import evoshift.composeapp.generated.resources.settings_requests
import evoshift.composeapp.generated.resources.settings_requests_days
import evoshift.composeapp.generated.resources.settings_requests_hours
import evoshift.composeapp.generated.resources.settings_unit_days
import evoshift.composeapp.generated.resources.settings_unit_hours
import org.jetbrains.compose.resources.StringResource
import org.jetbrains.compose.resources.stringResource

/** Test tags of the request limits' dialog. */
object RequestLimitsTags {
    const val DIALOG = "request_limits_dialog"
    fun field(field: RequestLimitField) = "request_limits_field_${field.name}"
    fun restore(group: RequestLimitGroup) = "request_limits_restore_${group.name}"
}

/** One number of the dialog: its value in [RequestSettings] and whether it counts hours. */
enum class RequestLimitField(val hours: Boolean, val read: (RequestSettings) -> Int) {
    BLOOD_DONATION_DAYS(false, { it.bloodDonationDays }),
    CHILDCARE_DAYS(false, { it.childcareDays }),
    CHILDCARE_HOURS(true, { it.childcareHours }),
    FORCE_MAJEURE_DAYS(false, { it.forceMajeureDays }),
    FORCE_MAJEURE_HOURS(true, { it.forceMajeureHours }),
    CARER_LEAVE_DAYS(false, { it.carerLeaveDays }),
    CARE_CHILD_DAYS(false, { it.careAllowanceChildDays }),
    CARE_FAMILY_DAYS(false, { it.careAllowanceFamilyDays }),
    CARE_TOTAL_DAYS(false, { it.careAllowanceTotalDays });

    /** The default (the law's value). */
    val default: Int get() = read(DEFAULTS)

    companion object {
        private val DEFAULTS = RequestSettings()

        /** [settings] with the numbers of [values] (a field left empty keeps its value), within bounds. */
        fun apply(settings: RequestSettings, values: Map<RequestLimitField, Int?>): RequestSettings {
            fun v(field: RequestLimitField) = values[field] ?: field.read(settings)
            return settings.copy(
                bloodDonationDays = v(BLOOD_DONATION_DAYS),
                childcareDays = v(CHILDCARE_DAYS),
                childcareHours = v(CHILDCARE_HOURS),
                forceMajeureDays = v(FORCE_MAJEURE_DAYS),
                forceMajeureHours = v(FORCE_MAJEURE_HOURS),
                carerLeaveDays = v(CARER_LEAVE_DAYS),
                careAllowanceChildDays = v(CARE_CHILD_DAYS),
                careAllowanceFamilyDays = v(CARE_FAMILY_DAYS),
                careAllowanceTotalDays = v(CARE_TOTAL_DAYS)
            ).clamped()
        }
    }
}

/** A section of the dialog: one request type's numbers. */
enum class RequestLimitGroup(val fields: List<RequestLimitField>) {
    BLOOD_DONATION(listOf(RequestLimitField.BLOOD_DONATION_DAYS)),
    CHILDCARE(listOf(RequestLimitField.CHILDCARE_DAYS, RequestLimitField.CHILDCARE_HOURS)),
    FORCE_MAJEURE(listOf(RequestLimitField.FORCE_MAJEURE_DAYS, RequestLimitField.FORCE_MAJEURE_HOURS)),
    CARER_LEAVE(listOf(RequestLimitField.CARER_LEAVE_DAYS)),
    CARE_ALLOWANCE(listOf(RequestLimitField.CARE_CHILD_DAYS, RequestLimitField.CARE_FAMILY_DAYS, RequestLimitField.CARE_TOTAL_DAYS))
}

private fun RequestLimitGroup.titleRes(): StringResource = when (this) {
    RequestLimitGroup.BLOOD_DONATION -> Res.string.settings_blood_donation_days
    RequestLimitGroup.CHILDCARE -> Res.string.settings_childcare_limit
    RequestLimitGroup.FORCE_MAJEURE -> Res.string.settings_force_majeure_limit
    RequestLimitGroup.CARER_LEAVE -> Res.string.settings_carer_leave_days
    RequestLimitGroup.CARE_ALLOWANCE -> Res.string.settings_care_allowance_days
}

/** What the numbers do in the app, and the law's defaults. */
@Composable
private fun RequestLimitGroup.explanation(): String = when (this) {
    RequestLimitGroup.BLOOD_DONATION -> stringResource(Res.string.settings_limit_explain_blood, RequestLimitField.BLOOD_DONATION_DAYS.default)
    RequestLimitGroup.CHILDCARE -> stringResource(Res.string.settings_limit_explain_childcare, RequestLimitField.CHILDCARE_DAYS.default, RequestLimitField.CHILDCARE_HOURS.default)
    RequestLimitGroup.FORCE_MAJEURE ->
        stringResource(Res.string.settings_limit_explain_force_majeure, RequestLimitField.FORCE_MAJEURE_DAYS.default, RequestLimitField.FORCE_MAJEURE_HOURS.default)
    RequestLimitGroup.CARER_LEAVE -> stringResource(Res.string.settings_limit_explain_carer, RequestLimitField.CARER_LEAVE_DAYS.default)
    RequestLimitGroup.CARE_ALLOWANCE -> stringResource(
        Res.string.settings_limit_explain_care,
        RequestLimitField.CARE_CHILD_DAYS.default, RequestLimitField.CARE_FAMILY_DAYS.default, RequestLimitField.CARE_TOTAL_DAYS.default
    )
}

private fun RequestLimitField.labelRes(): StringResource = when (this) {
    RequestLimitField.BLOOD_DONATION_DAYS -> Res.string.settings_limit_blood_field
    RequestLimitField.CHILDCARE_DAYS, RequestLimitField.FORCE_MAJEURE_DAYS -> Res.string.settings_requests_days
    RequestLimitField.CHILDCARE_HOURS, RequestLimitField.FORCE_MAJEURE_HOURS -> Res.string.settings_requests_hours
    RequestLimitField.CARER_LEAVE_DAYS -> Res.string.settings_limit_year_field
    RequestLimitField.CARE_CHILD_DAYS -> Res.string.settings_care_child
    RequestLimitField.CARE_FAMILY_DAYS -> Res.string.settings_care_family
    RequestLimitField.CARE_TOTAL_DAYS -> Res.string.settings_care_total
}

/** The narrowest an outlined field gets before the fields stack: its short label and unit fit on one line. */
private val MinFieldWidth: Dp = 150.dp
private val FieldGap: Dp = 12.dp

/**
 * SH-223 / SH-231: the request types' yearly numbers (blood donation, childcare, force majeure,
 * carer's leave, care allowance). Each type: a sentence-case title, one line on what the numbers
 * do in the app and the law's defaults, short labelled fields with their unit (side by side when
 * they fit, else one under another) and "Restore defaults" when a value differs from the law's.
 * Saving keeps the request types' rules ([RequestSettings.types], [RequestSettings.defaults]).
 */
@Composable
fun RequestSettingsDialog(
    initial: RequestSettings,
    onDismiss: () -> Unit,
    onSave: (RequestSettings) -> Unit
) {
    val start = initial.clamped()
    var texts by remember { mutableStateOf(RequestLimitField.entries.associateWith { it.read(start).toString() }) }
    EvoDialog(
        title = stringResource(Res.string.settings_requests),
        icon = Icons.Default.EventNote,
        onDismiss = onDismiss,
        modifier = Modifier.testTag(RequestLimitsTags.DIALOG),
        confirm = DialogAction(stringResource(Res.string.save), {
            onSave(RequestLimitField.apply(start, texts.mapValues { (_, text) -> text.toIntOrNull() }))
        })
    ) {
        Text(stringResource(Res.string.settings_help_requests), color = Color.White.copy(alpha = 0.85f), style = MaterialTheme.typography.bodyMedium)
        RequestLimitGroup.entries.forEach { group ->
            LimitGroup(
                group = group,
                texts = texts,
                onChange = { field, text -> texts = texts + (field to text) },
                onRestore = { texts = texts + group.fields.associateWith { it.default.toString() } }
            )
        }
    }
}

@Composable
private fun ColumnScope.LimitGroup(
    group: RequestLimitGroup,
    texts: Map<RequestLimitField, String>,
    onChange: (RequestLimitField, String) -> Unit,
    onRestore: () -> Unit
) {
    val title = stringResource(group.titleRes())
    val changed = group.fields.any { (texts[it]?.toIntOrNull() ?: it.default) != it.default }
    Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
        Text(
            title,
            style = MaterialTheme.typography.titleSmall,
            fontWeight = FontWeight.Bold,
            color = LightGold,
            modifier = Modifier.semantics { heading() }
        )
        Text(group.explanation(), style = MaterialTheme.typography.bodySmall, color = Color.White.copy(alpha = 0.8f))
        BoxWithConstraints(Modifier.fillMaxWidth()) {
            val count = group.fields.size
            val fits = maxWidth >= MinFieldWidth * count + FieldGap * (count - 1)
            val field: @Composable (RequestLimitField, Modifier) -> Unit = { f, modifier ->
                EvoTextField(
                    value = texts[f].orEmpty(),
                    onValueChange = { onChange(f, it.filter { c -> c.isDigit() }.take(3)) },
                    label = stringResource(f.labelRes()),
                    suffix = stringResource(if (f.hours) Res.string.settings_unit_hours else Res.string.settings_unit_days),
                    keyboardType = KeyboardType.Number,
                    modifier = modifier.testTag(RequestLimitsTags.field(f))
                )
            }
            if (fits) {
                Row(Modifier.fillMaxWidth(), horizontalArrangement = Arrangement.spacedBy(FieldGap)) {
                    group.fields.forEach { field(it, Modifier.weight(1f)) }
                }
            } else {
                Column(Modifier.fillMaxWidth(), verticalArrangement = Arrangement.spacedBy(8.dp)) {
                    group.fields.forEach { field(it, Modifier.fillMaxWidth()) }
                }
            }
        }
        if (changed) {
            val description = stringResource(Res.string.settings_limits_restore_for, title)
            TextButton(
                onClick = onRestore,
                modifier = Modifier.align(Alignment.End).handCursor().testTag(RequestLimitsTags.restore(group)).semantics { contentDescription = description }
            ) {
                Icon(Icons.Default.Restore, contentDescription = null, tint = LightGold, modifier = Modifier.size(18.dp))
                Spacer(Modifier.width(6.dp))
                Text(stringResource(Res.string.settings_limits_restore), color = LightGold, fontWeight = FontWeight.Bold)
            }
        }
    }
}
